import { projectService } from '@/services';
import { router } from '@/router';

const state = {
  items: [],
  statuses: {},
  detail: {
    state: {},
    provider: { key: '' },
    providerSubject: {},
    grantProgram: { key: '' },
    role: {},
    responsibleFaculty: {},
    solver: { key: '' },
    isNonResearchProject: false,
    internationalProject: false,
    mainFieldGroup: { key: '' },
    mainField: { key: '' },
    category: { key: '' },
    secondaryField: { key: '' },
    otherField: { key: '' },
    studyProgram: {},
    socioEconomicBenefitPrimaryArea: '',
    predominantFundingSource: {},
    projectType: {},
    budget: {}
  },
  projectLog: {
    items: [],
    totalCount: 0,
    search: {
      pageRows: 10,
      actualPage: 1,
      orderBy: '',
      orderByAsc: false
    }
  },
  totalCount: 0,
  search: {
    filter: {
      startedOn: null,
      endedOn: null,
      name: ''
    },
    pageRows: 10,
    actualPage: 1,
    orderBy: '',
    orderByAsc: false
  }
};

const actions = {
  getAll({ commit, dispatch }: any, searchQuery: any) {
    commit('saveSearchQuery', searchQuery);

    projectService
      .getAll(searchQuery)
      .then(
        projects => commit('getAllSuccess', projects),
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  getById({ commit, dispatch }: any, id: any) {
    projectService
      .getById(id)
      .then(
        project => {
          commit('getByIdSuccess', project);
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  getProjectAvailableStates({ commit, dispatch }: any, id: any) {
    projectService
      .getProjectAvailableStates(id)
      .then(
        statuses => {
          commit('getProjectAvailableStatesSuccess', statuses);
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  createProject({ dispatch }: any, { newProject }: any) {
    projectService
      .createProject(newProject)
      .then(
        () => {
          dispatch('alert/success', `notification.projectCreated`, {
            root: true
          });

          router.push('/');
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  updateProject({ dispatch }: any, { id, project }: any) {
    projectService
      .updateProject(id, project)
      .then(
        () => {
          dispatch('getProjectAvailableStates', id);
          dispatch('alert/success', `notification.projectUpdated`, {
            root: true
          });
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  deleteProject({ dispatch }: any, { id }: any) {
    projectService
      .deleteProject(id)
      .then(
        () => {
          dispatch('getAll', state.search);
          dispatch('alert/success', `notification.projectDeleted`, {
            root: true
          });
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  sendStateChangeRequest({ commit, dispatch }: any, { id, stateRequest }: any) {
    projectService
      .sendStatechangeRequest(id, stateRequest)
      .then(
        project => {
          commit('getByIdSuccess', project);
          dispatch('getProjectAvailableStates', id);
          dispatch('alert/success', `notification.projectStateChangeRequest`, {
            root: true
          });
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  resolveStateChangeRequest({ commit, dispatch }: any, { id, stateRequest }: any) {
    projectService
      .resolveStateChangeRequest(id, stateRequest)
      .then(
        project => {
          commit('getByIdSuccess', project);
          dispatch('getProjectAvailableStates', id);
          dispatch('alert/success', `notification.projectStateChangeResult`, {
            root: true
          });
        },
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  clearProject({ commit, dispatch }: any) {
    commit('clearProject');
  },
  getLog({ commit, dispatch }: any, { projectId, searchQuery }: any) {
    commit('saveLogSearchQuery', searchQuery);

    projectService
      .getLog(projectId, searchQuery)
      .then(
        logItems => commit('getLogAllSuccess', logItems),
        error => {
          dispatch('alert/error', JSON.stringify(error), {
            root: true
          });
        }
      )
      .finally();
  },
  uploadAttachments({ dispatch, commit }: any, { projectId, files }: any) {
    dispatch('application/setLoading', true, { root: true });
    projectService
      .uploadAttachments(projectId, files)
      .then(
        attachments => {
          commit('saveAttachmentsSuccess', attachments.data);
          dispatch('alert/success', `notification.attachmentsUploaded`, {
            root: true
          });
        },
        error => {
          commit('saveAttachmentsFailure', error);
          dispatch('alert/error', `notification.attachmentsUploadedError`, {
            root: true
          });
        }
      )
      .finally(() => dispatch('application/setLoading', false, { root: true }));
  },

  downloadAttachment({ dispatch, commit }: any, { projectId, trustedFileName, fileName, id }: any) {
    projectService.downloadAttachment(projectId, trustedFileName, fileName, id);
  },

  deleteAttachment({ dispatch, commit }: any, { projectId, id }: any) {
    dispatch('application/setLoading', true, { root: true });
    projectService
      .deleteAttachment(projectId, id)
      .then(
        () => {
          commit('deleteAttachmentSuccess', id);
          dispatch('alert/success', `notification.attachmentDeleted`, {
            root: true
          });
        },
        error => {
          commit('deleteAttachmentFailure', error);
          dispatch('alert/error', `notification.attachmentDeletedError`, {
            root: true
          });
        }
      )
      .finally(() => dispatch('application/setLoading', false, { root: true }));
  }
};

const mutations = {
  getAllSuccess(state: any, projects: any) {
    state.totalCount = projects.totalCount;
    state.items = projects;
  },
  getByIdSuccess(state: any, project: any) {
    if (!project.secondaryField) {
      project.secondaryField = { key: '' };
    }

    if (!project.otherField) {
      project.otherField = { key: '' };
    }

    if (!project.solver) {
      project.solver = { key: '' };
    }

    state.detail = project;
  },
  getProjectAvailableStatesSuccess(state: any, statuses: any) {
    state.statuses = statuses;
  },
  saveSearchQuery(state: any, searchQuery: any) {
    state.search = searchQuery;
  },
  saveLogSearchQuery(state: any, searchQuery: any) {
    state.projectLog.search = searchQuery;
  },
  clearProject(state: any) {
    state.detail = {};
    state.projectLog = {
      items: [],
      totalCount: 0,
      search: {
        pageRows: 10,
        actualPage: 1,
        orderBy: '',
        orderByAsc: false
      }
    };
  },
  getLogAllSuccess(state: any, logItems: any) {
    state.projectLog.totalCount = logItems.totalCount;
    state.projectLog.items = logItems;
  },
  saveAttachmentsSuccess(state: any, attachments: any) {
    state.detail.attachments = attachments;
  },
  saveAttachmentsFailure(state: any, error: any) {
    state = { error };
  },
  deleteAttachmentSuccess(state: any, id: number) {
    state.detail.attachments = state.detail.attachments.filter(function(el: any) {
      return el.id != id;
    });
  },
  deleteAttachmentFailure(state: any, error: any) {
    state = { error };
  }
};

const getters = {
  getProjects: (state: any, getters: any) => () => {
    return state.items;
  },
  getAvailableStatuses: (state: any, getters: any) => () => {
    return state.statuses.length ? state.statuses?.filter((s: any) => s.mode === 'Approved') : [];
  },
  getAvailableOnRequestStatuses: (state: any, getters: any) => () => {
    return state.statuses.length ? state.statuses?.filter((s: any) => s.mode === 'OnRequest') : [];
  },
  getTotalCount: (state: any, getters: any) => () => {
    return state.totalCount;
  },
  getLogItems: (state: any, getters: any) => () => {
    return state.projectLog.items;
  },
  getLogItemsCount: (state: any, getters: any) => () => {
    return state.projectLog.totalCount;
  },
  getProject: (state: any, getters: any) => () => {
    return state.detail;
  }
};

export const project = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
